import { Store, EventBus, DataRouter } from "@xbs/lib-state";
import { getViewById, registerViews } from "./registeredViews";
import { defaultEditorShape, defaultCalendars, defaultColors } from "./consts";
import { normalizeEditor, normalizeConfig, uid, normalizeCalendars, } from "./helpers";
import { getEventsByBounds, normalizeEvent } from "./events";
import { isWithinInterval } from "date-fns";
import { checkIntegrity } from "./package/package";
import { getRecurringById } from "./recurring";
export default class DataStore extends Store {
    constructor(w) {
        super(w);
        checkIntegrity();
        this._router = new DataRouter(super.setState.bind(this), [
            {
                in: ["mode", "date"],
                out: ["bounds"],
                exec: (ctx) => {
                    const { mode, date, config } = this.getState();
                    const view = this._registeredViews[mode];
                    if (!view)
                        return;
                    const bounds = view.getBounds(date, getViewById(config, mode)?.config || {});
                    this.setState({ bounds }, ctx);
                },
            },
            {
                in: ["events", "bounds", "calendars"],
                out: ["viewData"],
                exec: (ctx) => {
                    const { events, bounds, calendars, dateFnsLocale } = this.getState();
                    const disabledCalendars = calendars.filter(i => !i.active);
                    let viewData = [];
                    if (bounds) {
                        const filteredEvents = events.filter(a => a.start_date < bounds[1] &&
                            (a.DTEND > bounds[0] ||
                                a.end_date > bounds[0]) &&
                            !disabledCalendars.find(c => c.id === a.type));
                        viewData = getEventsByBounds(filteredEvents, bounds, disabledCalendars, dateFnsLocale);
                    }
                    this.setState({ viewData }, ctx);
                },
            },
            {
                in: ["mode", "bounds", "viewData", "calendars"],
                out: ["viewModel"],
                exec: (ctx) => {
                    const { date, bounds, mode, viewData, config, calendars, } = this.getState();
                    const view = this._registeredViews[mode];
                    // TODO: can we do it only when we have a mode?
                    if (view) {
                        const viewModel = view.getViewModel(date, bounds, {
                            ...(getViewById(config, mode)?.config ||
                                {}),
                            autoSave: config.autoSave,
                        }, viewData, calendars
                            .slice()
                            .sort((a, b) => +b.active - +a.active));
                        this.setState({ viewModel }, ctx);
                    }
                },
            },
            {
                in: ["mode", "bounds"],
                out: ["datepicker"],
                exec: (ctx) => {
                    const { date, bounds } = this.getState();
                    const markers = (day) => {
                        return isWithinInterval(day, {
                            start: bounds[0],
                            end: bounds[1],
                        })
                            ? "inrange"
                            : "";
                    };
                    const datepicker = {
                        current: date,
                        markers,
                    };
                    this.setState({ datepicker }, ctx);
                },
            },
        ], {
            config: normalizeConfig,
        });
        const inBus = (this.in = new EventBus());
        inBus.on("set-mode", ({ value }) => {
            this.setState({ mode: value });
        });
        inBus.on("set-date", ({ value }) => {
            this.setState({ date: new Date(value) });
        });
        inBus.on("update-event", ({ event, id }) => {
            const { events, calendars, config } = this.getState();
            const prevEvent = events.filter(i => i.id === id)[0];
            if (!prevEvent)
                return;
            const newEvent = { ...prevEvent, ...event };
            const filtered = events.filter(i => i !== prevEvent);
            this.setState({
                events: [
                    ...filtered,
                    normalizeEvent(newEvent, calendars, config.timeStep),
                ],
            });
        });
        inBus.on("update-calendar", ({ calendar, id }) => {
            const { calendars, editorShape } = this.getState();
            const item = calendars.filter(i => i.id === id)[0];
            if (!item)
                return;
            copyObjectData(calendar, item);
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                const option = typeCombo.options.find(o => o.id === id);
                if (option) {
                    copyObjectData(calendar, option);
                }
            }
            this.setState({
                calendars,
                editorShape,
            });
        });
        inBus.on("add-calendar", ({ calendar }) => {
            const { calendars, colors, editorShape } = this.getState();
            const data = {
                id: uid(),
                ...calendar,
            };
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                typeCombo.options = [
                    ...typeCombo.options.filter(i => i.id !== data.id),
                    data,
                ];
            }
            this.setState({
                calendars: normalizeCalendars([...calendars, data], colors),
                editorShape,
            });
        });
        inBus.on("delete-calendar", ({ id }) => {
            const { calendars, events, editorShape } = this.getState();
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                typeCombo.options = typeCombo.options.filter(o => o.id !== id);
            }
            this.setState({
                calendars: calendars.filter(c => c.id !== id),
                events: events.filter((ev) => ev.type !== id),
                editorShape,
            });
        });
        inBus.on("add-event", ({ event }) => {
            const { events, calendars, config } = this.getState();
            if (!calendars.length) {
                inBus.exec("add-calendar", {
                    calendar: { label: "New Calendar" },
                });
            }
            this.setState({
                events: [
                    ...events,
                    normalizeEvent(event, calendars, config.timeStep),
                ],
            });
        });
        inBus.on("edit-event", (config) => {
            if (config) {
                let { selected } = this.getState();
                const { events, calendars } = this.getState();
                const { id, add } = config;
                if (add) {
                    selected = add;
                    if (!calendars.length) {
                        inBus.exec("add-calendar", {
                            calendar: { label: "New Calendar" },
                        });
                    }
                }
                else {
                    if (`${id}`.includes("wx_recurring")) {
                        selected = getRecurringById(id, events);
                    }
                    else {
                        selected =
                            events.find(item => item.id == id) || selected;
                    }
                }
                this.setState({
                    selected,
                    edit: add ? "add" : selected?.readonly ? false : "update",
                    popupInfo: null,
                });
            }
            else {
                const date = this.getState().date;
                this.setState({ edit: false, date });
            }
        });
        inBus.on("close-event-info", () => {
            this.setState({ popupInfo: null });
        });
        inBus.on(
        // probably should change to set 'set-next-interval' 'set-prev-interval' or something
        // since we won't have step different from 1 and -1
        "set-bound", ({ step }) => {
            const { mode, date, config } = this.getState();
            const view = this._registeredViews[mode];
            const nextDate = step > 0
                ? view.getNext(date, getViewById(config, mode)?.config || {})
                : view.getPrev(date, getViewById(config, mode)?.config || {});
            this.setState({ date: nextDate });
        });
        inBus.on("select-event", ({ id, popup }) => {
            const { events, viewData } = this.getState();
            let parent_id;
            if (`${id}`.includes("wx_recurring")) {
                parent_id = `${id}`.split("wx_recurring")[1];
            }
            let event = events.find(ev => ev.id == (parent_id || id));
            if (parent_id) {
                event = getRecurringById(id, events);
            }
            if (!event)
                return;
            const isHidden = !viewData.find(ev => ev.id === event.id);
            if (isHidden) {
                this.setState({
                    date: new Date(event.start_date),
                });
            }
            this.setState({
                selectedId: id,
                selected: event,
                popupInfo: !!popup,
            });
        });
        inBus.on("toggle-sidebar", (data) => {
            const { sidebar } = this.getState();
            if (!sidebar)
                return;
            this.setState({
                sidebar: data ? data : { show: !sidebar.show },
            });
        });
        inBus.on("delete-event", ({ id }) => {
            const { events } = this.getState();
            const filtered = events.filter(ev => ev.id !== id);
            this.setState({ events: filtered, edit: false });
        });
    }
    init(state) {
        const { colors } = state;
        const initData = {
            selected: null,
            selectedId: null,
            edit: false,
            popupInfo: null,
            dateRange: [],
            editorShape: defaultEditorShape,
            bounds: [new Date(), new Date()],
            sidebar: { show: true },
            ...state,
            datepicker: {
                current: new Date(),
                markers: null,
            },
            colors: colors?.length ? colors : defaultColors,
        };
        this._registeredViews = {};
        this._router.init(initData);
        this.normalizeState();
    }
    setState(state, ctx) {
        this._router.setState(state, ctx);
    }
    normalizeState() {
        const state = this.getState();
        const { config, colors } = state;
        let { events, calendars } = state;
        this._registeredViews = registerViews(config.views);
        calendars = calendars.length
            ? normalizeCalendars(calendars, colors)
            : defaultCalendars;
        events =
            events.length && calendars.length
                ? events.map(e => normalizeEvent(e, calendars, config.timeStep))
                : [];
        const editorShape = normalizeEditor({
            ...state,
            events,
            calendars,
            config,
        });
        this.setState({ ...state, editorShape, events, calendars, config });
    }
}
function copyObjectData(source, target) {
    Object.keys(source).forEach(key => {
        if (key !== "id") {
            target[key] = source[key];
        }
    });
}
//# sourceMappingURL=DataStore.js.map